import dayjs from "dayjs";

/**
 * Direct payment ("استقبال المدفوعات بشكل مباشر") availability rules.
 *
 * Direct payment emails the guest a payment link together with the welcome
 * book, which is a per-property messaging template (the property's "guest
 * book"/دليل الضيف). So it needs two things: the host account must have payments
 * enabled, AND the SELECTED property must have a guest-book template assigned to
 * it.
 *
 * This mirrors the backend's per-property check
 * (ManualReservationController → TemplateService.HasTemplateForPropertyAsync):
 * a template counts for a property only when that property is in the template's
 * assigned `property` list. Previously the client only checked whether the host
 * owned ANY template, so a host with a guest book on one property saw the toggle
 * enabled on every property — and the backend then rejected the booking with its
 * "no guest book for this property" error. Both the toggle state and the
 * submitted value now come from these helpers, so what the host sees and what we
 * send cannot drift apart.
 */

/** A template item (from /api/get-user-templates) reduced to the assigned
 * properties we need to read. */
interface TemplateAssignment {
  property?: Array<{ propertyId?: number | null } | null> | null;
}

/** Whether a specific property has a guest-book template assigned to it. */
export function propertyHasGuestBook(
  templates: unknown,
  propertyId: number | undefined | null
): boolean {
  if (!propertyId || !Array.isArray(templates)) return false;
  return templates.some((template) => {
    const assigned = (template as TemplateAssignment)?.property;
    return (
      Array.isArray(assigned) &&
      assigned.some((p) => Number(p?.propertyId) === Number(propertyId))
    );
  });
}

/** Whether the host may use direct payment for THIS property. */
export function canUseDirectPayment(
  isHostPaymentActive: boolean | undefined,
  templates: unknown,
  propertyId: number | undefined | null
): boolean {
  return (
    Boolean(isHostPaymentActive) && propertyHasGuestBook(templates, propertyId)
  );
}

/**
 * The value to send as `isDirectPaymentAccepted`. Never true unless the host may
 * use direct payment for the selected property and actually left the toggle on.
 */
export function resolveDirectPaymentAccepted(
  isHostPaymentActive: boolean | undefined,
  templates: unknown,
  propertyId: number | undefined | null,
  isToggleOn: boolean | undefined
): boolean {
  return (
    canUseDirectPayment(isHostPaymentActive, templates, propertyId) &&
    Boolean(isToggleOn)
  );
}

/**
 * Whether the host has actually moved the check-out LATER than the booking's
 * current one — which is what makes this edit an extension rather than an
 * ordinary modification (`ReservationExtensionDetector.IsExtension` on the
 * backend applies the same test, among others).
 *
 * Accepts whatever the date picker holds: a "YYYY-MM-DD" string, a Date, or a
 * dayjs object — the submit handler already normalises through `dayjs(...)`, so
 * this reads them the same way. Anything unreadable answers false: a warning is
 * a claim about what will happen, and we do not make it on a date we cannot
 * read.
 */
export function isCheckOutExtended(
  originalCheckOut: unknown,
  requestedCheckOut: unknown
): boolean {
  // Checked before dayjs sees them: `dayjs(undefined)` is NOW, and a valid one —
  // so a missing value would silently compare today's date against the booking's
  // and warn about an extension nobody asked for.
  if (originalCheckOut == null || requestedCheckOut == null) return false;

  const original = dayjs(originalCheckOut as never);
  const requested = dayjs(requestedCheckOut as never);
  if (!original.isValid() || !requested.isValid()) return false;

  return requested.startOf("day").isAfter(original.startOf("day"));
}

/**
 * Whether to show the "guest gets a Msool link for the extra nights only, the
 * original stay stays settled with you directly" warning on the edit-reservation
 * booking-data tab.
 *
 * Three conditions, and all three are load-bearing:
 *
 * - The check-out has actually moved LATER. Without this the warning fired on
 *   any edit of an off-platform booking with the toggle on — a phone-number fix,
 *   a name correction — promising the guest "a payment link for the extra
 *   nights" when there are no extra nights and no link is coming.
 * - The toggle is on, so the extension will bill the guest through Msool.
 * - The PARENT stay was never itself settled through Msool
 *   (`isDirectPaymentAccepted` false on the loaded reservation — an MS-588
 *   booking the host collects for themselves reads false here).
 *
 * Together they are the one case worth flagging: Msool is about to bill the
 * guest for the extra nights while the rest of the stay is still being settled
 * outside Msool.
 */
export function shouldWarnOffPlatformExtension(
  directPaymentOn: boolean | undefined,
  parentSettledThroughMsool: boolean | undefined,
  checkOutExtended: boolean | undefined
): boolean {
  return (
    Boolean(checkOutExtended) &&
    Boolean(directPaymentOn) &&
    !parentSettledThroughMsool
  );
}
